// The Incidents button on an active driver's record.
//
// Since 5 October 2026 its first section is disciplinary incidents: the
// Disciplinary Incident Typeform moved into the cockpit, reported against the
// Code of Conduct section 6 of the v6 agreement (db/migrations/4400 to 4403,
// docs/DISCIPLINARY-INCIDENTS.md). The same form is on the Disciplinary page.
// The operational incidents and the management log below it are unchanged.
import { useState } from 'react'
import { ShieldAlert, Plus, AlertCircle, Gavel } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { Input } from '../../lib/shadcn/input'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../lib/shadcn/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from './Chip'
import { useGetDriverIncidents, useAddDriverIncident, useGetDriverOperationalIncidents } from '../../hooks/backend/interviews'
import { useGetDriverDisciplinaryIncidents } from '../../hooks/backend/disciplinary'
import { IncidentReportDialog } from '../disciplinary/IncidentReportDialog'
import { IncidentCard } from '../disciplinary/IncidentCard'
import type { Incident } from '../../utils/disciplinaryIncidents'
import {
  type DriverIncident,
  type DriverOperationalIncident,
  formatDate,
  formatZar,
  statusLabel,
  statusTone,
} from '../../utils/interviews'

const CATEGORIES = [
  'Accident',
  'Complaint',
  'Vehicle damage',
  'Conduct',
  'Documentation',
  'Other',
] as const

const SEVERITIES = [
  { code: 'Low', tone: 'neutral' as const },
  { code: 'Medium', tone: 'amber' as const },
  { code: 'High', tone: 'red' as const },
] as const

function severityTone(severity: string): 'neutral' | 'amber' | 'red' {
  const match = SEVERITIES.find((s) => s.code.toLowerCase() === severity.toLowerCase())
  return match?.tone ?? 'neutral'
}

function todayIso(): string {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function IncidentsDialog({
  applicationId,
  canAct,
}: {
  applicationId: string
  canAct: boolean
}) {
  const { data, loading, error, trigger } = useGetDriverIncidents()
  const ops = useGetDriverOperationalIncidents()
  const disc = useGetDriverDisciplinaryIncidents<{ driver_id: string | null; driver: string | null; incidents: Incident[] }>()
  const { trigger: triggerAdd } = useAddDriverIncident()
  const [open, setOpen] = useState(false)
  const [reporting, setReporting] = useState(false)
  const [category, setCategory] = useState<string>(CATEGORIES[0])
  const [severity, setSeverity] = useState<string>('Low')
  const [incidentDate, setIncidentDate] = useState(todayIso())
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)

  const incidents = (data as DriverIncident[] | undefined) ?? []
  const opsIncidents = (ops.data as DriverOperationalIncident[] | undefined) ?? []
  const discIncidents = disc.data?.incidents ?? []
  const totalCount = incidents.length + opsIncidents.length + discIncidents.length

  const opsTrigger = ops.trigger
  const discTrigger = disc.trigger
  const loadDisc = () => { void discTrigger({ application_id: applicationId }, { skipCache: true }).catch(() => undefined) }
  const load = () => {
    trigger({ application_id: applicationId }, { skipCache: true })
    opsTrigger({ application_id: applicationId }, { skipCache: true })
    loadDisc()
  }

  const handleOpen = () => {
    setOpen(true)
    setCategory(CATEGORIES[0])
    setSeverity('Low')
    setIncidentDate(todayIso())
    setDescription('')
    load()
  }

  const handleAdd = async () => {
    setBusy(true)
    try {
      await triggerAdd({
        application_id: applicationId,
        category,
        severity,
        description: description.trim() || null,
        incident_date: incidentDate,
      }).result
      toast.success('Incident logged')
      setDescription('')
      load()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error('Could not log incident', { description: msg })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Button
        variant="outline"
        onClick={handleOpen}
        className="relative h-11 w-full justify-center sm:h-9 sm:w-auto"
      >
        <ShieldAlert className="h-4 w-4" />
        Incidents
        {totalCount > 0 && (
          <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[hsl(var(--brand))] px-1.5 text-[11px] font-semibold text-[hsl(var(--brand-foreground))]">
            {totalCount}
          </span>
        )}
      </Button>

      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v && !busy) setOpen(false)
        }}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Driver incidents</DialogTitle>
            <DialogDescription>
              Logged incidents for this driver, newest first.
            </DialogDescription>
          </DialogHeader>

          {error ? (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : loading && totalCount === 0 ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
              <Spinner className="h-4 w-4" /> Loading incidents…
            </div>
          ) : (
            <div className="space-y-4">
              {/* Disciplinary incidents (clause 27, Code of Conduct section 6) */}
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Disciplinary incidents
                  </h4>
                  {disc.data?.driver_id && (
                    <Button size="sm" variant="brand" onClick={() => setReporting(true)}>
                      <Gavel className="mr-1 h-3.5 w-3.5" /> Report a disciplinary incident
                    </Button>
                  )}
                </div>
                {disc.error ? (
                  <p className="text-sm text-destructive">{disc.error}</p>
                ) : disc.loading && !disc.data ? (
                  <p className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner className="h-3.5 w-3.5" /> Loading…</p>
                ) : discIncidents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No disciplinary incident reported. Reporting one tells the driver nothing: his manager reads it, sees the step the agreement suggests, and releases it or closes it.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {discIncidents.map((i) => <IncidentCard key={i.id} i={i} onChanged={loadDisc} compact />)}
                  </div>
                )}
              </div>

              {/* Operational incidents (from operations, read-only) */}
              <div className="space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Operational incidents
                </h4>
                {opsIncidents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No operational incidents recorded.</p>
                ) : (
                  <ul className="space-y-2">
                    {opsIncidents.map((inc) => (
                      <li key={inc.id} className="rounded-lg border border-border bg-card p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-foreground">
                            {inc.title || inc.incident_type || 'Incident'}
                          </span>
                          <div className="flex items-center gap-2">
                            {inc.severity && <Chip tone={severityTone(inc.severity)}>{inc.severity}</Chip>}
                            {inc.status && <Chip tone={statusTone(inc.status)}>{statusLabel(inc.status)}</Chip>}
                            <span className="text-xs text-muted-foreground">{formatDate(inc.occurred_at)}</span>
                          </div>
                        </div>
                        {Number(inc.cost_zar) > 0 && (
                          <p className="mt-1 text-xs font-medium text-foreground">
                            Cost: {formatZar(inc.cost_zar)}
                          </p>
                        )}
                        {inc.description && (
                          <p className="mt-1.5 text-sm text-muted-foreground">{inc.description}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Management log (manually logged incidents) */}
              <div className="space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Management log
                </h4>
                {incidents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No incidents logged yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {incidents.map((inc) => (
                      <li key={inc.id} className="rounded-lg border border-border bg-card p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-foreground">{inc.category}</span>
                          <div className="flex items-center gap-2">
                            <Chip tone={severityTone(inc.severity)}>{inc.severity}</Chip>
                            <span className="text-xs text-muted-foreground">{formatDate(inc.incident_date)}</span>
                          </div>
                        </div>
                        {inc.description && (
                          <p className="mt-1.5 text-sm text-muted-foreground">{inc.description}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {canAct && (
            <div className="space-y-3 border-t border-border pt-4">
              <h4 className="text-sm font-semibold text-foreground">Add a note to the management log</h4>
              <p className="text-xs text-muted-foreground">For a note that is not a disciplinary matter. Misconduct goes through Report a disciplinary incident above, so it reaches his manager with the step the agreement suggests.</p>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="incident-category" className="text-xs font-medium text-muted-foreground">
                    Category
                  </Label>
                  <Select value={category} onValueChange={setCategory} disabled={busy}>
                    <SelectTrigger id="incident-category" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="incident-severity" className="text-xs font-medium text-muted-foreground">
                    Severity
                  </Label>
                  <Select value={severity} onValueChange={setSeverity} disabled={busy}>
                    <SelectTrigger id="incident-severity" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SEVERITIES.map((s) => (
                        <SelectItem key={s.code} value={s.code}>
                          {s.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="incident-date" className="text-xs font-medium text-muted-foreground">
                  Date
                </Label>
                <Input
                  id="incident-date"
                  type="date"
                  value={incidentDate}
                  onChange={(e) => setIncidentDate(e.target.value)}
                  disabled={busy}
                  className="w-full sm:max-w-xs"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="incident-description" className="text-xs font-medium text-muted-foreground">
                  Description
                </Label>
                <Textarea
                  id="incident-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What happened?"
                  disabled={busy}
                  rows={3}
                />
              </div>

              <Button
                onClick={handleAdd}
                disabled={busy || !incidentDate}
                className="h-11 w-full justify-center sm:h-9 sm:w-auto"
              >
                {busy ? <Spinner className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                Add incident
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <IncidentReportDialog
        open={reporting}
        onOpenChange={setReporting}
        driverId={disc.data?.driver_id ?? null}
        driverName={disc.data?.driver ?? null}
        onReported={loadDisc}
      />
    </>
  )
}
